import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Button,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  OutlinedInput,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Location, Link as RouterLink, useLocation, useNavigate } from "react-router";
import { apiBaseUrls, cacheService, env, searchService, userService } from "../../Core/Api/services";
import { LoginRequest } from "../../Core/Api/Types/login-request";
import LogLayout from "../../Layouts/Log";
import { routePaths } from "../../Router/router";
import useCustomSnackbar from "../../Utils/Hooks/useCustomSnackbar";
import { useToggle } from "../../Utils/Hooks/useToggle";
import { PlatformMode } from "../../Core/Api/Types/platform-mode";
import { AuthMode } from "../../Core/Api/Types/auth-mode";
import { OktaAuth } from "@okta/okta-auth-js";
import { useDispatch } from "react-redux";
import { InitState } from "../../Redux/Reducers/redux-slice-asset";
import { AclTypes } from "../../Core/Api/Types/acl-types";
import { SearchRequestNative } from "../../Core/Api/Types/search-request-native";
import { ObjectLayer } from "../../Core/Api/Types/object-layer";
import { UserHasAcl } from "../../Utils/helpers";
import pkceChallenge from "pkce-challenge";
import { ModalContext } from "../../Components/Modals";
import ModalConfirmCof, {
  ModalConfirmCofElementProps,
} from "../../Components/Modals/ModalConfirmCof/modal-confirm-cof";
import ModalMfaValidateToken, {
  ModalMfaValidateTokenElementProps,
} from "../../Components/Modals/ModalMfaValidateToken/modal-mfa-validate-token";
import { LoginResponse } from "../../Core/Api/Types/login-response";
import { useTranslation } from "react-i18next";

const Login = () => {
  const { t } = useTranslation();
  const { active: visiblePassword, toggle: togglePasswordVisibility } = useToggle(false);
  const { register, handleSubmit } = useForm<LoginRequest>();
  const [adfsUrl, setAdfsUrl] = useState<string | null>(null);
  const [oktaLogin, setOktaLogin] = useState<boolean>(false);
  const [oktaClientId, setOktaClientId] = useState<string>("");
  const [oktaCallBackUrlRoute, setOktaCallBackUrlRoute] = useState<string>("");
  const [oktaDomain, setOktaDomain] = useState<string>("");
  const [oktaPlatformId, setOktaPlatformId] = useState<number | null>();
  const [loggingIn, setLoggingIn] = useState(false);
  const navigate = useNavigate();
  const enqueueCustomSnackbar = useCustomSnackbar();
  const location: Location = useLocation();
  const dispatch = useDispatch();

  // Modals
  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };

  // Okta login
  const onSubmitLoginWithOkta = useCallback(() => {
    if (oktaClientId === null || oktaCallBackUrlRoute === null || oktaDomain === null || !oktaPlatformId) {
      return;
    }

    const oktaAuth = new OktaAuth({
      clientId: oktaClientId,
      issuer: "https://" + oktaDomain + "/oauth2/default",
      redirectUri: oktaCallBackUrlRoute,
      scopes: ["openid", "profile", "email"],
      pkce: true,
    });

    pkceChallenge().then((pkce) => {
      cacheService.SetOktaCodeVerifier(pkce.code_verifier, oktaPlatformId);
      console.log("okta::codeVerifier", pkce.code_verifier);
      console.log("okta::platformId", oktaPlatformId);

      oktaAuth.signInWithRedirect({
        codeVerifier: pkce.code_verifier,
        codeChallenge: pkce.code_challenge,
      });
    });
  }, [oktaClientId, oktaCallBackUrlRoute, oktaDomain, oktaPlatformId]);

  const onLoginCallback = (login: string | null, password: string | null, response: LoginResponse) => {
    if (response.success) {
      userService.SetDataOnLoginSuccess().then(async function () {
        setLoggingIn(false);

        if (response.group?.acls?.includes(AclTypes.aclLockEverything)) {
          navigate(routePaths.lockedUser.path);
          return;
        }
        if (response.group?.acls?.includes(AclTypes.aclRestrictViewBundleOnly)) {
          navigate(routePaths.inventoryBundlesDashboard.path);
          return;
        }

        if (
          cacheService.IsDriveEnabled() &&
          (UserHasAcl(AclTypes.aclDrive) || cacheService.GetPlatformMode() === PlatformMode.Browser) &&
          response.platformHeadObjectId
        ) {
          cacheService.SetPlatformDriveHeadId(response.platformHeadObjectId);
        }

        let nav =
          response.platformMode === PlatformMode.Browser
            ? routePaths.drive.path
            : location.state?.from?.pathname || routePaths.home.path;

        console.debug("Redirecting to", nav);
        dispatch(InitState());
        navigate(nav);
      });
    } else {
      setLoggingIn(false);

      if (response.rescode === -2000) {
        openModal &&
          openModal<ModalConfirmCofElementProps>(ModalConfirmCof, {
            onConfirm: () => {
              userService.Login(new LoginRequest({ login, password })).then((response) => {
                onLoginCallback(login, password, response);
              });
            },
          });
      }

      if (response.rescode === -10) {
        navigate(routePaths.loginNewPassword.path, { state: { login: login, password: password } });
        enqueueCustomSnackbar(t("alert:warning.lastPasswordIsTooOld"), "warning");
      }

      if (response.rescode === -900 && login && password) {
        setLoggingIn(true);
        // open mfa modal
        openModal &&
          openModal<ModalMfaValidateTokenElementProps>(ModalMfaValidateToken, {
            login: login,
            password: password,
            onLoginCallback: onLoginCallback,
            onClose: () => setLoggingIn(false),
          });
      } else enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "warning");
    }
  };

  // Local login
  const onSubmitLoginLocalAccount = handleSubmit(({ login, password }) => {
    setLoggingIn(true);
    cacheService.UnsetAuth();
    userService.Login(new LoginRequest({ login, password })).then((response) => {
      onLoginCallback(login, password, response);
    });
  });

  // Main loader
  useEffect(() => {
    userService.GetAuthModes(window.location.hostname).then((response) => {
      if (response?.success) {
        // Store potential ADFS url endpoint (if found)
        if (response.authModes && response.authModes.filter((x) => x.authMode === AuthMode.ADFS).length === 1) {
          let samlRequestUrl = apiBaseUrls[env] + "adfs-service/api/binding-post/" + response.objectId;
          setAdfsUrl(samlRequestUrl);
        }

        // Store potential Okta url endpoint (if found)
        let oktaSettings = response.authModes?.find((x) => x.authMode === AuthMode.OKTA);

        if (
          oktaSettings &&
          oktaSettings?.oktaCallBackUrlRoute &&
          oktaSettings?.oktaClientId &&
          oktaSettings?.oktaEndpoint
        ) {
          setOktaCallBackUrlRoute(oktaSettings?.oktaCallBackUrlRoute);
          setOktaClientId(oktaSettings?.oktaClientId);
          setOktaDomain(oktaSettings?.oktaEndpoint);
          setOktaPlatformId(response.objectId);
          setOktaLogin(true);
        }

        if (response.catchPhrase) cacheService.SetCatchPhrase(response.catchPhrase);
      }
    });
  }, []);

  return (
    <LogLayout
      sxRightContainer={{ display: "flex", flexDirection: "column", justifyContent: "center" }}
      catchSentence={cacheService.GetCatchPhrase()}
    >
      <Stack alignItems="center">
        <Typography variant="h3" textAlign="center">
          {t("page:body.pleaseLoginToContinue")}
        </Typography>
      </Stack>
      <Divider sx={{ mt: 2, mb: 4 }} />
      <form onSubmit={onSubmitLoginLocalAccount}>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel shrink htmlFor="email" sx={{ flex: 1 }}>
            {t("common:login")}*
          </InputLabel>
          <OutlinedInput {...register("login")} size="medium" id="email" />
        </FormControl>
        <FormControl fullWidth sx={{ mb: 5 }}>
          <InputLabel shrink htmlFor="password" sx={{ flex: 1 }}>
            {t("common:password")}*
          </InputLabel>
          <OutlinedInput
            {...register("password")}
            type={visiblePassword ? "text" : "password"}
            autoComplete="password"
            required
            id="password"
            endAdornment={
              <IconButton sx={{ mr: -1 }} onClick={togglePasswordVisibility}>
                {visiblePassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            }
          />
        </FormControl>
        <Stack alignItems="center">
          <Stack
            flexWrap={"nowrap"}
            flexDirection="row"
            justifyContent={"space-around"}
            width="100%"
            alignItems={"center"}
          >
            <Button type="submit" variant="contained" fullWidth size="large" loading={loggingIn}>
              {t("common:login")}
            </Button>

            {oktaLogin && (
              <>
                <Typography textAlign="center" sx={{ mr: 4, ml: 4 }}>
                  {t("common:or")}
                </Typography>
                <Button onClick={onSubmitLoginWithOkta} variant="contained" fullWidth size="large">
                  {t("page:body.loginWithOkta")}
                </Button>
              </>
            )}

            {adfsUrl !== null && (
              <>
                <Typography textAlign="center" sx={{ mr: 4, ml: 4 }}>
                  {t("common:or")}
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={() => {
                    window.location.href = adfsUrl;
                  }}
                >
                  {t("button:loginWithAzure")}
                </Button>
              </>
            )}
          </Stack>

          <Link
            underline="hover"
            component={RouterLink}
            to={routePaths.loginForgotPasswordRequest.path}
            sx={{ mt: 4, color: "text.primary", opacity: "0.7" }}
          >
            {t("button:forgotYourPassword")}
          </Link>
        </Stack>
      </form>
    </LogLayout>
  );
};

export default Login;
